/**
 * Host adapter — inject app-specific wallet, auth, and navigation.
 * Components never import BookForge / TextForge stores directly.
 */

import type {
  GpuInstance,
  GpuNavTarget,
  ModelTaskGuide,
  ToastLevel,
  WorkUnit,
} from "./types.js";
import type { SelectedLora } from "./lora.js";
import type { GetAccessToken } from "./client.js";
import type {
  Connection,
  ConnectionPurpose,
  ConnectionTab,
} from "./connections.js";

export interface GpuPluginWallet {
  balanceUsd: number;
  checkoutBusy?: boolean;
  isMember: boolean;
  memberPriceUsd: number;
  subscriptionLabel?: string | null;
}

export interface GpuPluginHost {
  /** Shown in copy */
  productName: string;
  /** forge-backend or suite relay base URL */
  forgeApiUrl: string;
  /** ProxyPro origin for main dump ∩ app filter. Default proxy.vividlex.com. */
  dumpCatalogUrl?: string;
  getAccessToken?: GetAccessToken;

  wallet: GpuPluginWallet;
  instance?: GpuInstance | null;
  /** Disable Connect while the host is writing the pref */
  connecting?: boolean;

  workUnits?: WorkUnit[];
  workTitle?: string;
  tasks?: ModelTaskGuide[];
  favoritesStorageKey?: string;

  /**
   * Fired only on Connect / Use. Host should persist via
   * /api/suite/connections/select (plugin does that when appId + purpose are set)
   * then route generate through this row.
   */
  onConnect?: (connection: Connection) => void | Promise<void>;
  /** Preview only — must not write prefs. */
  onHighlightModel?: (
    modelId: string,
    meta?: { name?: string; modality?: string; provider?: string }
  ) => void;

  appId?: string;
  /** AccessHub tool_id the picker starts on. GpuBrowser lists every model tool; Connect writes that tool. Default PRIMARY_TOOL[appId]. */
  accessHubTool?: string;
  /** Fired when AccessHub has a dump row assigned for this tool. */
  onAccessHubModel?: (
    modelId: string,
    meta: { name?: string; dumpId?: string; toolId?: string; selector?: boolean },
  ) => void;
  purpose?: ConnectionPurpose;
  visibleTabs?: ConnectionTab[];
  /**
   * Resolved catalog tab map from AccessHub / suite wallet.
   * Missing keys inherit plugin defaults. `civitai_red` is 18+ only.
   */
  catalogTabs?: Partial<Record<ConnectionTab | "civitai_red" | "hf_lora", boolean>>;
  /** Operator-attested 18+ on AccessHub. Required for CivitAI Red. */
  adultOk?: boolean;
  /** Explicit Red unlock. Defaults to adultOk && catalogTabs.civitai_red. */
  allowCivitaiRed?: boolean;

  onToast?: (level: ToastLevel, message: string, durationMs?: number) => void;
  onRefreshWallet?: () => void | Promise<void>;
  onBuyPack?: (amountUsd: number) => void | Promise<void>;
  onSubscribeMember?: () => void | Promise<void>;
  onNavigate?: (target: GpuNavTarget) => void;
  onReady?: () => void;

  connectCtaLabel?: string;
  hfCtaLabel?: string;
  cloudflareCtaLabel?: string;
  civitaiCtaLabel?: string;

  /**
   * Modalities allowed on Connect.
   * Default: chat only (`text-generation`).
   * Media apps: `["text-to-video","image-to-video"]`, `["text-to-speech"]`,
   * `["text-to-image"]`, or `"all"`.
   */
  connectModalities?: string[] | "all";
  excludeCatalogModel?: (
    modelId: string,
    meta?: { name?: string; modality?: string }
  ) => boolean;
  defaultInferenceModality?:
    | "all"
    | "text-generation"
    | "text-to-speech"
    | "text-to-image"
    | "text-to-video"
    | "image-to-video"
    | "other";

  /**
   * LoRA stack (CivitAI and, when AccessHub `catalogTabs.hf_lora` is on, HF).
   * Hosts persist this; the picker never treats a LoRA as the generate engine.
   */
  loraStack?: SelectedLora[];
  loraCapable?: boolean;
  connectedEngineBaseModel?: string | null;
  onLorasChange?: (stack: SelectedLora[]) => void;
}

export type ModalityFamily = "chat" | "tts" | "image" | "video" | "other";

/** One family per product job — chat apps must not list image/video rows. */
export function modalityFamily(mod: string | undefined): ModalityFamily {
  const m = (mod || "").toLowerCase().replace(/\s+/g, "-");
  if (
    m.includes("speech") ||
    m === "tts" ||
    m.includes("asr") ||
    m.includes("audio")
  ) {
    return "tts";
  }
  if (m.includes("video") || m === "t2v" || m === "i2v") return "video";
  if (m.includes("image")) return "image";
  if (
    m.includes("text-generation") ||
    m.includes("chat") ||
    m === "llm" ||
    m === "instruct"
  ) {
    return "chat";
  }
  return "other";
}

export function hostAllowsConnect(
  host: GpuPluginHost,
  modality: string | undefined
): boolean {
  const allowed = host.connectModalities;
  if (allowed === "all") return true;
  const fam = modalityFamily(modality);
  if (!allowed || !allowed.length) {
    return fam === "chat";
  }
  return allowed.some((a) => modalityFamily(a) === fam);
}

/** Modalities this app may list. Scoped hosts never return `"all"`. */
export function hostListedModalities(
  host: GpuPluginHost
): string[] | "all" {
  if (host.connectModalities === "all") return "all";
  if (host.connectModalities?.length) return host.connectModalities;
  return ["text-generation"];
}

export function hostAllowsCivitaiRed(host: GpuPluginHost): boolean {
  if (host.allowCivitaiRed === true) return true;
  if (host.allowCivitaiRed === false) return false;
  return Boolean(host.adultOk && host.catalogTabs?.civitai_red);
}

/** AccessHub Models → Forge HF LoRA picker. Default off. */
export function hostAllowsHfLora(host: GpuPluginHost): boolean {
  return host.catalogTabs?.hf_lora === true;
}

export function hostVisibleCatalogTabs(host: GpuPluginHost): ConnectionTab[] {
  const all: ConnectionTab[] = ["production", "hf", "cloudflare", "civitai", "forge_endpoint"];
  if (host.visibleTabs?.length) {
    return all.filter((t) => host.visibleTabs!.includes(t));
  }
  const ct = host.catalogTabs;
  if (!ct) return all;
  return all.filter((t) => ct[t] !== false);
}

export function toast(
  host: GpuPluginHost,
  level: ToastLevel,
  message: string,
  ms?: number
): void {
  host.onToast?.(level, message, ms);
  if (!host.onToast && typeof console !== "undefined") {
    console[level === "error" ? "error" : "info"](`[gpu-browser] ${message}`);
  }
}
